import { useEffect, useRef, useState } from 'react';

/**
 * Diktování (checklist A3 — MUST pro partu 50+): Web Speech API v češtině,
 * průběžný přepis do textového pole. Když prohlížeč API nemá, tlačítko se
 * nezobrazí a mistr píše — psaní je VŽDY dostupný fallback (wireframe 13 §1).
 */

interface VysledekReci {
  isFinal: boolean;
  0: { transcript: string };
}

interface UdalostReci {
  resultIndex: number;
  results: { length: number; [i: number]: VysledekReci };
}

interface RozpoznavaniReci {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: UdalostReci) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

type KonstruktorReci = new () => RozpoznavaniReci;

function najdiKonstruktor(): KonstruktorReci | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: KonstruktorReci;
    webkitSpeechRecognition?: KonstruktorReci;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface Diktovani {
  /** Prohlížeč umí Web Speech API — jinak tlačítko diktování vůbec nenabízet. */
  podporovano: boolean;
  diktuji: boolean;
  /** Zapne/vypne diktát; průběžný přepis chodí do `naText` (finální úseky). */
  prepni: () => void;
  /** Rozdiktovaný, ještě nepotvrzený úsek (zobrazit šedě za textem). */
  prubezny: string;
}

export function useDiktovani(naText: (text: string) => void): Diktovani {
  const Konstruktor = najdiKonstruktor();
  const [diktuji, setDiktuji] = useState(false);
  const [prubezny, setPrubezny] = useState('');
  const rozpoznavani = useRef<RozpoznavaniReci | null>(null);
  const naTextRef = useRef(naText);
  naTextRef.current = naText;

  useEffect(
    () => () => {
      rozpoznavani.current?.stop();
    },
    [],
  );

  if (!Konstruktor) {
    return { podporovano: false, diktuji: false, prepni: () => undefined, prubezny: '' };
  }

  const prepni = () => {
    if (diktuji) {
      rozpoznavani.current?.stop();
      rozpoznavani.current = null;
      setDiktuji(false);
      setPrubezny('');
      return;
    }
    const r = new Konstruktor();
    r.lang = 'cs-CZ';
    r.continuous = true;
    r.interimResults = true;
    r.onresult = (e) => {
      let rozdiktovano = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const vysledek = e.results[i];
        if (!vysledek) continue;
        const text = vysledek[0].transcript;
        if (vysledek.isFinal) naTextRef.current(text.trim());
        else rozdiktovano += text;
      }
      setPrubezny(rozdiktovano);
    };
    // Diktát selže (hluk, mikrofon) → přepis končí, text zůstává k domluvení
    // rukou (wireframe 13 §1 edge case) — žádná blokace.
    r.onerror = () => {
      setDiktuji(false);
      setPrubezny('');
      rozpoznavani.current = null;
    };
    r.onend = () => {
      setDiktuji(false);
      setPrubezny('');
      rozpoznavani.current = null;
    };
    rozpoznavani.current = r;
    r.start();
    setDiktuji(true);
  };

  return { podporovano: true, diktuji, prepni, prubezny };
}
